import { useState, useEffect } from 'react';
import { FilterableSelect } from '../components/common/FilterableSelect';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../auth/useAuth';
import {
  usePreferences,
  useUpdatePreferences,
  type UserPreferences,
} from '../api/queries/preferences';
import { useCreditsBalance } from '../api/queries/credits';
import { useUiStore, type ThemeMode } from '../stores/ui.store';

/* ------------------------------------------------------------------ */
/*  Tab Panel helper                                                   */
/* ------------------------------------------------------------------ */
interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

/* ------------------------------------------------------------------ */
/*  POV / Tense options                                                */
/* ------------------------------------------------------------------ */
const POV_OPTIONS = [
  { value: '', label: '(none)' },
  { value: 'first-person', label: 'First Person' },
  { value: 'second-person', label: 'Second Person' },
  { value: 'third-person-limited', label: 'Third Person Limited' },
  { value: 'third-person-omniscient', label: 'Third Person Omniscient' },
];

const TENSE_OPTIONS = [
  { value: '', label: '(none)' },
  { value: 'past', label: 'Past Tense' },
  { value: 'present', label: 'Present Tense' },
  { value: 'future', label: 'Future Tense' },
];

/* ------------------------------------------------------------------ */
/*  Keyboard shortcuts data                                            */
/* ------------------------------------------------------------------ */
const SHORTCUTS = [
  { keys: 'Cmd+K', description: 'Command Palette' },
  { keys: 'Cmd+S', description: 'Save' },
  { keys: 'Cmd+Z', description: 'Undo' },
  { keys: 'Cmd+Shift+Z', description: 'Redo' },
  { keys: 'Cmd+/', description: 'AI Slash Menu' },
  { keys: 'Cmd+Shift+N', description: 'New Chapter' },
  { keys: 'Cmd+Shift+F', description: 'Focus Mode' },
  { keys: 'Cmd+B', description: 'Bold' },
  { keys: 'Cmd+I', description: 'Italic' },
  { keys: '?', description: 'Keyboard Shortcuts Help' },
  { keys: 'Escape', description: 'Deselect / Close panel' },
];

/* ------------------------------------------------------------------ */
/*  Preferences form type                                              */
/* ------------------------------------------------------------------ */
interface PreferencesForm {
  theme: 'light' | 'dark' | 'system' | 'high-contrast';
  defaultPov: string;
  defaultTense: string;
  defaultNarrativeStyle: string;
}

/* ------------------------------------------------------------------ */
/*  Settings Route                                                     */
/* ------------------------------------------------------------------ */
export function SettingsRoute() {
  const [tab, setTab] = useState(0);

  return (
    <Paper sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Profile" />
        <Tab label="Preferences" />
        <Tab label="AI Settings" />
        <Tab label="Credits & Usage" />
        <Tab label="Keyboard Shortcuts" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <ProfileTab />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <PreferencesTab />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <AiSettingsTab />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <CreditsTab />
      </TabPanel>
      <TabPanel value={tab} index={4}>
        <KeyboardShortcutsTab />
      </TabPanel>
    </Paper>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 1: Profile                                                     */
/* ------------------------------------------------------------------ */
function ProfileTab() {
  const { userId, email } = useAuth();
  const isMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

  return (
    <Stack spacing={3} sx={{ maxWidth: 600 }}>
      <Typography variant="h6">Profile</Typography>
      <Typography variant="body2" color="text.secondary">
        Your profile is managed through your authentication provider. These
        fields are read-only.
      </Typography>

      <Divider />

      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            User ID
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
            {userId}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Email
          </Typography>
          <Typography variant="body1">{email}</Typography>
        </Box>
      </Stack>

      {isMockAuth && (
        <Chip
          label="Mock Auth"
          color="warning"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        />
      )}
    </Stack>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 2: Preferences                                                 */
/* ------------------------------------------------------------------ */
function PreferencesTab() {
  const { data: prefs, isLoading } = usePreferences();
  const updatePrefs = useUpdatePreferences();
  const setThemeMode = useUiStore((s) => s.setThemeMode);
  const [snackOpen, setSnackOpen] = useState(false);

  const { control, handleSubmit, reset } = useForm<PreferencesForm>({
    defaultValues: {
      theme: 'light',
      defaultPov: '',
      defaultTense: '',
      defaultNarrativeStyle: '',
    },
  });

  // Populate form when preferences load
  useEffect(() => {
    if (prefs) {
      reset({
        theme: prefs.theme ?? 'light',
        defaultPov: prefs.defaultPov ?? '',
        defaultTense: prefs.defaultTense ?? '',
        defaultNarrativeStyle: prefs.defaultNarrativeStyle ?? '',
      });
    }
  }, [prefs, reset]);

  const onSubmit = (data: PreferencesForm) => {
    // Apply theme immediately
    setThemeMode(data.theme as ThemeMode);

    const payload: UserPreferences = {
      theme: data.theme,
      defaultPov: data.defaultPov || undefined,
      defaultTense: data.defaultTense || undefined,
      defaultNarrativeStyle: data.defaultNarrativeStyle || undefined,
    };
    updatePrefs.mutate(payload, {
      onSuccess: () => setSnackOpen(true),
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4} sx={{ maxWidth: 600 }}>
        <Typography variant="h6">Preferences</Typography>

        {/* Theme */}
        <FormControl component="fieldset">
          <FormLabel component="legend">Theme</FormLabel>
          <Controller
            name="theme"
            control={control}
            render={({ field }) => (
              <RadioGroup row {...field}>
                <FormControlLabel
                  value="light"
                  control={<Radio />}
                  label="Light"
                />
                <FormControlLabel
                  value="dark"
                  control={<Radio />}
                  label="Dark"
                />
                <FormControlLabel
                  value="system"
                  control={<Radio />}
                  label="System"
                />
                <FormControlLabel
                  value="high-contrast"
                  control={<Radio />}
                  label="High Contrast"
                />
              </RadioGroup>
            )}
          />
        </FormControl>

        <Divider />

        {/* Default book settings */}
        <Typography variant="h6">Default Book Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          These defaults are applied when creating a new book. They can be
          overridden per book and per chapter.
        </Typography>

        <Controller
          name="defaultPov"
          control={control}
          render={({ field }) => (
            <FilterableSelect
              options={POV_OPTIONS.filter((o) => o.value !== '')}
              value={field.value}
              onChange={field.onChange}
              label="Point of View"
              allowEmpty
              emptyLabel="(none)"
            />
          )}
        />

        <Controller
          name="defaultTense"
          control={control}
          render={({ field }) => (
            <FilterableSelect
              options={TENSE_OPTIONS.filter((o) => o.value !== '')}
              value={field.value}
              onChange={field.onChange}
              label="Tense"
              allowEmpty
              emptyLabel="(none)"
            />
          )}
        />

        <Controller
          name="defaultNarrativeStyle"
          control={control}
          render={({ field }) => (
            <TextField
              label="Narrative Style"
              placeholder="e.g. lyrical, minimalist, conversational"
              fullWidth
              {...field}
            />
          )}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={updatePrefs.isPending}
          sx={{ alignSelf: 'flex-start' }}
        >
          {updatePrefs.isPending ? 'Saving...' : 'Save Preferences'}
        </Button>

        {updatePrefs.isError && (
          <Alert severity="error">
            Failed to save preferences. Please try again.
          </Alert>
        )}
      </Stack>

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message="Preferences saved"
      />
    </form>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 3: AI Settings                                                 */
/* ------------------------------------------------------------------ */
function AiSettingsTab() {
  return (
    <Stack spacing={3} sx={{ maxWidth: 600 }}>
      <Typography variant="h6">AI Providers</Typography>
      <Typography variant="body2" color="text.secondary">
        Current AI provider configuration. Provider management is handled via
        environment variables.
      </Typography>

      <Divider />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Capability</strong>
              </TableCell>
              <TableCell>
                <strong>Provider</strong>
              </TableCell>
              <TableCell>
                <strong>Status</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Text Generation</TableCell>
              <TableCell>Anthropic Claude Sonnet</TableCell>
              <TableCell>
                <Chip label="Active" color="success" size="small" />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Image Generation</TableCell>
              <TableCell>Fal.ai FLUX</TableCell>
              <TableCell>
                <Chip label="Active" color="success" size="small" />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Audio / TTS</TableCell>
              <TableCell>Fal.ai Kokoro TTS</TableCell>
              <TableCell>
                <Chip label="Active" color="success" size="small" />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Embeddings</TableCell>
              <TableCell>Google Vertex AI</TableCell>
              <TableCell>
                <Chip label="Active" color="success" size="small" />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />

      <Typography variant="h6">Voice Anchors</Typography>
      <Typography variant="body2" color="text.secondary">
        Voice anchor configuration will be available in Sprint 2.
      </Typography>
      <Alert severity="info">
        Voice anchor management is planned for Sprint 2. This section will allow
        you to configure per-character voice profiles for audio generation.
      </Alert>
    </Stack>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 4: Credits & Usage                                             */
/* ------------------------------------------------------------------ */
function CreditsTab() {
  const { data, isLoading, isError } = useCreditsBalance();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load credit balance.</Alert>;
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 700 }}>
      <Typography variant="h6">Credits & Usage</Typography>

      {/* Balance */}
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="overline" color="text.secondary">
          Current Balance
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 700 }}>
          {data?.balance?.toLocaleString() ?? 0}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          credits
        </Typography>
      </Paper>

      {/* Transaction history */}
      {data?.recentTransactions && data.recentTransactions.length > 0 ? (
        <>
          <Typography variant="h6">Recent Transactions</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Date</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Type</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Amount</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Balance After</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recentTransactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell>
                      {new Date(tx.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tx.kind}
                        size="small"
                        color={tx.amount >= 0 ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: tx.amount >= 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {tx.amount >= 0 ? '+' : ''}
                      {tx.amount}
                    </TableCell>
                    <TableCell align="right">{tx.balanceAfter}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No transaction history yet.
        </Typography>
      )}
    </Stack>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 5: Keyboard Shortcuts                                          */
/* ------------------------------------------------------------------ */
function KeyboardShortcutsTab() {
  return (
    <Stack spacing={3} sx={{ maxWidth: 600 }}>
      <Typography variant="h6">Keyboard Shortcuts</Typography>
      <Typography variant="body2" color="text.secondary">
        Key bindings for the book editor. Custom remapping will be available in
        Sprint 8.
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 200 }}>
                <strong>Shortcut</strong>
              </TableCell>
              <TableCell>
                <strong>Action</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SHORTCUTS.map((s) => (
              <TableRow key={s.keys}>
                <TableCell>
                  <Chip
                    label={s.keys}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                </TableCell>
                <TableCell>{s.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
